import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Button,
  Center,
  Container,
  Flex,
  Grid,
  Heading,
  Icon,
  Input,
  Spinner,
  Stack,
  Text,
  Textarea,
} from '@chakra-ui/react';
import { PhotoIcon, PlusIcon } from '@heroicons/react/24/outline';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Field } from '@/components/ui/field';
import { SimpleSelect } from '@/components/ui/simple-select';
import { toaster } from '@/components/ui/toaster';
import { Breadcrumbs } from '@/components/ui/breadcrumb';
import ImageLightbox from '@/components/ui/image-lightbox';
import DestructiveModal from '@/components/alert-dialogs/destructive-modal';
import { SortableList } from '@/components/sortable-list';
import api from '@/utils/api';
import useCoursesStore from '@/utils/use-courses';
import {
  blankCourseDraft,
  emptyStagedAssignees,
  unwrap,
  CourseDraft,
  CourseItemDraft,
  StagedAssignees,
  uid,
} from '@/pages/courses/api';
import { buildPublicUrl } from '@/utils/public-url';
import { Course, CourseStatus } from '@/lib/types';
import AssignCoursePanel from '@/pages/courses/view/assign-course-panel';
import ModuleCard, { ModuleDraft } from './module-card';
import StepAccess from './step-access';
import RemindersCard from './reminders-card';

const STEPS = [
  { key: 'details', label: 'Details', hint: 'Title and description' },
  { key: 'content', label: 'Content', hint: 'Modules and items' },
  { key: 'access', label: 'Access & tracking', hint: 'Who can open it' },
  { key: 'publish', label: 'Publish', hint: 'Review and go live' },
] as const;

type StepKey = (typeof STEPS)[number]['key'];

const STEP_KEYS = STEPS.map((step) => step.key);

const AUDIENCE_ITEMS = ['Coaches only', 'Parents only', 'Coaches & parents'].map((audience) => ({
  label: audience,
  value: audience,
}));

function moduleDraftsFromCourse(course: Course): ModuleDraft[] {
  return (course.modules ?? []).map((module) => ({
    id: module.id,
    name: module.name,
    instructions: module.instructions,
    assignment_text: module.assignment_text,
    assignment_shortcut: module.assignment_shortcut,
    items: (module.items ?? []).map((item) => ({
      uid: uid(),
      id: item.id,
      content_id: item.content_id,
      content_reference_id: item.content_reference_id,
      content_type_id: item.content_type_id,
      content_type_name: item.content_type_name,
      content_name: item.content_name,
      source_label: item.source_label,
    })),
  }));
}

// StepRail is the wizard header from the prototype: numbered chips with a
// label + hint, the active one tinted with the brand soft fill.
function StepRail({
  active,
  courseExists,
  onStep,
}: {
  active: StepKey;
  courseExists: boolean;
  onStep: (step: StepKey) => void;
}) {
  return (
    <Flex
      gap={2}
      bg='bg.panel'
      borderWidth='1px'
      borderColor='border'
      borderRadius='lg'
      shadow='sm'
      p={2.5}
      mb={5}
    >
      {STEPS.map((step, index) => {
        const isActive = step.key === active;
        // Everything after Details needs a saved course to hang off.
        const isEnabled = step.key === 'details' || courseExists;
        return (
          <Flex
            key={step.key}
            flex='1'
            align='center'
            gap={2.5}
            px={3}
            py={2.5}
            borderRadius='md'
            bg={isActive ? 'brand.soft' : undefined}
            opacity={isEnabled ? 1 : 0.45}
            cursor={isEnabled ? 'pointer' : 'not-allowed'}
            minW={0}
            onClick={() => isEnabled && onStep(step.key)}
          >
            <Center
              w='26px'
              h='26px'
              borderRadius='full'
              bg={isActive ? 'brand.solid' : 'bg.subtle'}
              color={isActive ? 'brand.contrast' : 'fg.muted'}
              fontFamily='heading'
              fontSize='xs'
              fontWeight='700'
              flexShrink={0}
            >
              {index + 1}
            </Center>
            <Box minW={0} display={{ base: 'none', md: 'block' }}>
              <Text
                fontFamily='heading'
                fontSize='xs'
                fontWeight='700'
                textTransform='uppercase'
                letterSpacing='0.08em'
                color={isActive ? 'brand.fg' : 'fg.muted'}
              >
                {step.label}
              </Text>
              <Text fontSize='xs' color='fg.muted' truncate>
                {step.hint}
              </Text>
            </Box>
          </Flex>
        );
      })}
    </Flex>
  );
}

// Mirrors the backend's cover constraints so a staged file fails fast instead
// of erroring at create time.
const MAX_COVER_BYTES = 10 * 1024 * 1024;
const COVER_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

// Course length presets from the design prototype; anything else is "Custom".
const COURSE_LENGTH_PRESETS = [15, 30, 45, 60];
const COURSE_LENGTH_ITEMS = [
  ...COURSE_LENGTH_PRESETS.map((minutes) => ({
    label: `${minutes} minutes`,
    value: String(minutes),
  })),
  { label: 'Custom', value: 'custom' },
];

// Course builder shell. Serves /courses/create (blank draft) and
// /courses/:courseId/build (existing course loaded, ?step= selects the step).
export default function CourseBuilder() {
  const { workspaceId = '', courseId } = useParams<{ workspaceId: string; courseId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    fetchCourse,
    saveCourse,
    setCourseStatus,
    createModule,
    updateModule,
    deleteModule,
    sortModules,
    saveModuleItems,
  } = useCoursesStore();

  const [draft, setDraft] = useState<CourseDraft>(() => blankCourseDraft(Number(workspaceId)));
  const [modules, setModules] = useState<ModuleDraft[]>([]);
  const [status, setStatus] = useState<CourseStatus>('draft');
  // Currently published version; zero until the first publish. Publishing
  // again cuts version + 1.
  const [version, setVersion] = useState(0);
  // The course's share-link capability, loaded with the course.
  const [referenceId, setReferenceId] = useState('');
  // Assignees picked while the course is still a draft — the API refuses
  // assigning an unpublished course, so these apply the moment publish
  // succeeds.
  const [stagedAssignees, setStagedAssignees] = useState<StagedAssignees>(emptyStagedAssignees());
  const [trackingLocked, setTrackingLocked] = useState(false);
  const [step, setStep] = useState<StepKey>(() => {
    const requested = searchParams.get('step') as StepKey | null;
    return requested && STEP_KEYS.includes(requested) && courseId ? requested : 'details';
  });
  const [isLoading, setIsLoading] = useState(Boolean(courseId));
  const [isSaving, setIsSaving] = useState(false);
  const [savedNote, setSavedNote] = useState('');
  // Ledger note for the next publish — the "what changed" line in the version
  // history. Cleared after each publish so v3's note doesn't inherit v2's.
  const [publishNote, setPublishNote] = useState('');
  const [courseName, setCourseName] = useState('');
  const [isUploadingCover, setIsUploadingCover] = useState(false);
  const [isCoverViewerOpen, setIsCoverViewerOpen] = useState(false);
  // True while the user has explicitly chosen "Custom" in the length select;
  // a loaded non-preset value also renders as custom without this flag.
  const [customLength, setCustomLength] = useState(false);
  // Cover picked before the draft exists: held locally (with a preview object
  // URL) and uploaded right after the first save mints a course id.
  const [pendingCover, setPendingCover] = useState<{ file: File; url: string } | null>(null);
  const [moduleToRemove, setModuleToRemove] = useState<ModuleDraft | null>(null);
  const coverInputRef = useRef<HTMLInputElement | null>(null);
  // Guards module saves against firing while a field edit is mid-flight.
  const savingModules = useRef(false);

  useEffect(() => {
    if (!courseId) return;
    fetchCourse(Number(courseId))
      .then((course) => {
        if (!course) {
          toaster.create({ title: 'Course not found', type: 'error' });
          navigate(`/${workspaceId}/courses`);
          return;
        }
        setCourseName(course.name);
        setStatus(course.status);
        setVersion(course.version);
        setReferenceId(course.reference_id);
        setTrackingLocked(course.tracking_locked);
        setDraft({
          id: course.id,
          workspace_id: course.workspace_id,
          name: course.name,
          description: course.description,
          audience_type: course.audience_type,
          estimated_minutes: course.estimated_minutes,
          cover_image: course.cover_image,
          access_mode: course.access_mode,
          tracking_mode: course.tracking_mode,
        });
        setModules(moduleDraftsFromCourse(course));
      })
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courseId]);

  // Revokes the staged cover's object URL when it is replaced or on unmount.
  useEffect(() => {
    return () => {
      if (pendingCover) URL.revokeObjectURL(pendingCover.url);
    };
  }, [pendingCover]);

  const noteSaved = useCallback(() => setSavedNote('Draft saved just now'), []);

  const patch = useCallback((changes: Partial<CourseDraft>) => {
    setDraft((prev) => ({ ...prev, ...changes }));
  }, []);

  const crumbs = useMemo(
    () => [
      { label: 'Courses', href: `/${workspaceId}/courses` },
      { label: courseId ? courseName || 'Edit course' : 'New course' },
    ],
    [workspaceId, courseId, courseName],
  );

  // -------------------------------------------------------------------------
  // Details step persistence
  // -------------------------------------------------------------------------

  const handleSaveDetails = async (goToContent: boolean) => {
    if (!draft.name.trim()) {
      toaster.create({ title: 'Give the course a title first', type: 'error' });
      return;
    }
    setIsSaving(true);
    try {
      const saved = await saveCourse({ ...draft, name: draft.name.trim() });
      noteSaved();
      setCourseName(saved.name);
      if (!draft.id) {
        // First save: flush any cover staged before the course had an id,
        // then move onto the canonical /build URL for this course.
        let coverImage = saved.cover_image;
        if (pendingCover) {
          try {
            coverImage = await uploadCover(saved.id, pendingCover.file);
          } catch (error: any) {
            toaster.create({
              title:
                error.response?.data?.error ||
                'Draft created, but the cover image failed to upload',
              description: 'You can add it again from the Details step.',
              type: 'error',
            });
          }
          setPendingCover(null);
        }
        navigate(`/${workspaceId}/courses/${saved.id}/build?step=content`, { replace: true });
        setDraft((prev) => ({ ...prev, id: saved.id, cover_image: coverImage }));
        setStep('content');
        return;
      }
      if (goToContent) setStep('content');
    } catch {
      // The store already surfaced the API error message.
    } finally {
      setIsSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Content step persistence — each action persists immediately, so the
  // header's "Draft saved just now" is always literally true.
  // -------------------------------------------------------------------------

  // Cover uploads go straight to the dedicated endpoint (multipart), which
  // stores the file on S3 and persists the course in one round trip.
  const uploadCover = async (courseId: number, file: File): Promise<string> => {
    const form = new FormData();
    form.append('file', file);
    const res = await api.post(`courses/${courseId}/cover`, form, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return unwrap<Course>(res).cover_image;
  };

  // Entry point for the file picker. With a saved draft the file uploads
  // immediately; on an unsaved course it is staged and previewed locally, then
  // uploaded by handleSaveDetails once "Create draft" succeeds.
  const handleCoverSelect = async (file: File) => {
    if (!COVER_MIME_TYPES.includes(file.type)) {
      toaster.create({ title: 'Use a JPG, PNG, WebP or GIF image', type: 'error' });
      return;
    }
    if (file.size > MAX_COVER_BYTES) {
      toaster.create({ title: 'Cover image must be 10MB or smaller', type: 'error' });
      return;
    }
    if (!draft.id) {
      setPendingCover({ file, url: URL.createObjectURL(file) });
      return;
    }
    setIsUploadingCover(true);
    try {
      const coverImage = await uploadCover(draft.id, file);
      patch({ cover_image: coverImage });
      noteSaved();
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to upload cover image',
        type: 'error',
      });
    } finally {
      setIsUploadingCover(false);
    }
  };

  const handleCoverRemove = async () => {
    if (pendingCover) {
      setPendingCover(null); // effect revokes the object URL
      return;
    }
    const next = { ...draft, cover_image: '' };
    setDraft(next);
    try {
      await saveCourse(next);
      noteSaved();
    } catch {
      setDraft(draft); // store surfaced the error
    }
  };

  // Access/tracking picks persist immediately — like every content action.
  const handleAccessPick = async (changes: Partial<CourseDraft>) => {
    const next = { ...draft, ...changes };
    setDraft(next);
    try {
      await saveCourse(next);
      noteSaved();
    } catch {
      setDraft(draft); // store surfaced the error (e.g. 409 mode locked)
    }
  };

  // First publish and re-publish share one path: both are "cut the next
  // version". The difference is only what the toast says and whether staged
  // assignees go out (a live course assigns live, so there are none).
  const handlePublish = async () => {
    if (!draft.id) return;
    const isRepublish = status === 'published';
    setIsSaving(true);
    try {
      const published = await setCourseStatus(draft.id, 'published', publishNote.trim());
      setStatus('published');
      setVersion(published.version);
      setPublishNote('');

      // Assignees staged during the draft go out now that there is a
      // published course to open. A failure here must not swallow the
      // publish: the course IS live, so say what still needs doing.
      const staged = stagedAssignees;
      let assignNote = '';
      if (staged.users.length > 0 || staged.groups.length > 0) {
        try {
          await api.post(`courses/${draft.id}/assignments`, {
            user_ids: staged.users.map((user) => user.id),
            group_ids: staged.groups.map((group) => group.id),
          });
          setStagedAssignees(emptyStagedAssignees());
          const count = staged.users.length + staged.groups.length;
          assignNote = ` Assigned to ${count} ${count === 1 ? 'target' : 'targets'}.`;
        } catch {
          assignNote = ' Assigning failed — assign from the course page.';
        }
      }

      toaster.create({
        title: isRepublish ? `Version ${published.version} published` : 'Course published',
        description: isRepublish
          ? `Version ${published.version} is recorded in the history — learners keep their progress.`
          : `Version ${published.version} is live — learners can open it from the hub.${assignNote}`,
        type: assignNote.includes('failed') ? 'warning' : 'success',
      });
      navigate(`/${workspaceId}/courses/${draft.id}`);
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to publish',
        type: 'error',
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddModule = async () => {
    if (!draft.id) return;
    try {
      const created = await createModule(draft.id, {
        name: `Module ${modules.length + 1}`,
        instructions: '',
        assignment_text: '',
        assignment_shortcut: '',
      });
      setModules((prev) => [
        ...prev,
        {
          id: created.id,
          name: created.name,
          instructions: created.instructions,
          assignment_text: created.assignment_text,
          assignment_shortcut: created.assignment_shortcut,
          items: [],
        },
      ]);
      noteSaved();
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to add module',
        type: 'error',
      });
    }
  };

  const handleModuleFieldsChange = (changed: ModuleDraft) => {
    setModules((prev) => prev.map((m) => (m.id === changed.id ? changed : m)));
  };

  const handleModuleFieldsBlur = async (module: ModuleDraft) => {
    if (savingModules.current) return;
    savingModules.current = true;
    try {
      await updateModule(module.id, {
        name: module.name,
        instructions: module.instructions,
        assignment_text: module.assignment_text.trim(),
        assignment_shortcut: module.assignment_shortcut,
      });
      noteSaved();
    } catch (error: any) {
      toaster.create({
        title: error.response?.data?.error || 'Failed to save module',
        type: 'error',
      });
    } finally {
      savingModules.current = false;
    }
  };

  const handleItemsChange = async (module: ModuleDraft, items: CourseItemDraft[]) => {
    const previous = modules;
    // Optimistic: reorder/add/remove renders immediately, rolls back on error.
    setModules((prev) => prev.map((m) => (m.id === module.id ? { ...m, items } : m)));
    try {
      const saved = await saveModuleItems(module.id, items);
      // Adopt server ids for inserted rows so the next save updates in place
      // (position-aligned: the endpoint returns rows in the order sent).
      setModules((prev) =>
        prev.map((m) =>
          m.id === module.id
            ? {
                ...m,
                items: items.map((item, index) => ({ ...item, id: saved[index]?.id ?? item.id })),
              }
            : m,
        ),
      );
      noteSaved();
    } catch (error: any) {
      setModules(previous);
      toaster.create({
        title: error.response?.data?.error || 'Failed to save items',
        type: 'error',
      });
    }
  };

  const handleConfirmRemoveModule = async () => {
    const module = moduleToRemove;
    if (!module) return;
    setModuleToRemove(null);
    const previous = modules;
    setModules((prev) => prev.filter((m) => m.id !== module.id));
    try {
      await deleteModule(module.id);
      noteSaved();
    } catch (error: any) {
      setModules(previous);
      toaster.create({
        title: error.response?.data?.error || 'Failed to remove module',
        type: 'error',
      });
    }
  };

  const handleReorderModules = async (reordered: { id: number; draft: ModuleDraft }[]) => {
    if (!draft.id) return;
    const previous = modules;
    const next = reordered.map((wrapped) => wrapped.draft);
    setModules(next);
    try {
      await sortModules(
        draft.id,
        next.map((m) => m.id),
      );
      noteSaved();
    } catch (error: any) {
      setModules(previous);
      toaster.create({
        title: error.response?.data?.error || 'Failed to reorder modules',
        type: 'error',
      });
    }
  };

  if (isLoading) {
    return (
      <Center minH='50vh'>
        <Spinner size='lg' />
      </Center>
    );
  }

  return (
    <Box minH='100vh' mx='auto' width={{ md: '90%' }} maxW='6xl' px={{ base: 4, md: 0 }} py={6}>
      <Container>
        <Breadcrumbs items={crumbs} />

        <Flex align='flex-end' justify='space-between' gap={4} mt={2} mb={5}>
          <Heading as='h1' size='3xl'>
            {draft.name || 'New course'}
          </Heading>
          <Flex align='center' gap={3} flexShrink={0}>
            {savedNote && (
              <Text fontSize='xs' color='fg.muted'>
                {savedNote}
              </Text>
            )}
            <Button
              variant='outline'
              size='sm'
              onClick={() =>
                navigate(
                  draft.id ? `/${workspaceId}/courses/${draft.id}` : `/${workspaceId}/courses`,
                )
              }
            >
              {draft.id ? 'Save & exit' : 'Cancel'}
            </Button>
          </Flex>
        </Flex>

        <StepRail active={step} courseExists={Boolean(draft.id)} onStep={setStep} />

        {step === 'details' && (
          <>
            <Grid templateColumns={{ base: '1fr', lg: 'minmax(0,1fr) 320px' }} gap={5}>
              <Box
                bg='bg.panel'
                borderWidth='1px'
                borderColor='border'
                borderRadius='lg'
                shadow='sm'
                p={6}
              >
                <Flex direction='column' gap={5}>
                  <Field label='Course title' required>
                    <Input
                      value={draft.name}
                      onChange={(e) => patch({ name: e.target.value })}
                      placeholder='e.g. Flag Football Coaching 101'
                      maxLength={200}
                    />
                  </Field>

                  <Field
                    label='Description'
                    helperText='Shown to learners on the course card and at the top of the course.'
                  >
                    <Textarea
                      rows={4}
                      value={draft.description}
                      onChange={(e) => patch({ description: e.target.value })}
                    />
                  </Field>

                  <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={4}>
                    <Field label='Audience type'>
                      <SimpleSelect
                        items={AUDIENCE_ITEMS}
                        value={draft.audience_type}
                        onValueChange={(value) => patch({ audience_type: value })}
                        placeholder='Choose an audience …'
                      />
                    </Field>

                    <Field label='Course length'>
                      {(() => {
                        const isPreset = COURSE_LENGTH_PRESETS.includes(draft.estimated_minutes);
                        const selectValue =
                          customLength || (draft.estimated_minutes > 0 && !isPreset)
                            ? 'custom'
                            : draft.estimated_minutes > 0
                              ? String(draft.estimated_minutes)
                              : '';
                        return (
                          <Flex direction='column' gap={2} w='100%'>
                            <SimpleSelect
                              items={COURSE_LENGTH_ITEMS}
                              value={selectValue}
                              onValueChange={(value) => {
                                if (value === 'custom') {
                                  setCustomLength(true);
                                  return;
                                }
                                setCustomLength(false);
                                patch({ estimated_minutes: value ? Number(value) : 0 });
                              }}
                              placeholder='Choose a length …'
                            />
                            {selectValue === 'custom' && (
                              <Input
                                type='number'
                                min={0}
                                max={1440}
                                value={draft.estimated_minutes || ''}
                                onChange={(e) =>
                                  patch({ estimated_minutes: Number(e.target.value) || 0 })
                                }
                                placeholder='Minutes, e.g. 25'
                              />
                            )}
                          </Flex>
                        );
                      })()}
                    </Field>
                  </Grid>
                </Flex>
              </Box>

              <Box
                bg='bg.panel'
                borderWidth='1px'
                borderColor='border'
                borderRadius='lg'
                shadow='sm'
                p={5}
                alignSelf='start'
              >
                <Text
                  fontFamily='heading'
                  fontSize='2xs'
                  fontWeight='700'
                  textTransform='uppercase'
                  letterSpacing='0.16em'
                  color='fg.muted'
                  mb={3}
                >
                  Cover image
                </Text>

                <input
                  ref={coverInputRef}
                  type='file'
                  accept='image/jpeg,image/png,image/webp,image/gif'
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) handleCoverSelect(file);
                    e.target.value = '';
                  }}
                />

                {draft.cover_image || pendingCover ? (
                  <>
                    <Box
                      aspectRatio='16 / 9'
                      borderRadius='md'
                      backgroundImage={`url(${draft.cover_image || pendingCover?.url})`}
                      backgroundSize='cover'
                      backgroundPosition='center'
                      cursor='zoom-in'
                      onClick={() => setIsCoverViewerOpen(true)}
                    />
                    <ImageLightbox
                      src={draft.cover_image || pendingCover?.url || ''}
                      alt={`${draft.name || 'Course'} cover image`}
                      isOpen={isCoverViewerOpen}
                      onClose={() => setIsCoverViewerOpen(false)}
                    />
                    <Flex gap={2} mt={3}>
                      <Button
                        variant='outline'
                        size='sm'
                        onClick={() => coverInputRef.current?.click()}
                        loading={isUploadingCover}
                      >
                        Replace
                      </Button>
                      <Button variant='ghost' size='sm' onClick={handleCoverRemove}>
                        Remove
                      </Button>
                    </Flex>
                    {pendingCover && (
                      <Text fontSize='xs' color='fg.muted' mt={2}>
                        Uploads when you create the draft.
                      </Text>
                    )}
                  </>
                ) : (
                  <Center
                    aspectRatio='16 / 9'
                    borderRadius='md'
                    bg='bg.subtle'
                    borderWidth='1px'
                    borderStyle='dashed'
                    borderColor='border.emphasized'
                    flexDirection='column'
                    gap={2.5}
                    cursor='pointer'
                    onClick={() => coverInputRef.current?.click()}
                  >
                    {isUploadingCover ? (
                      <Spinner size='md' />
                    ) : (
                      <>
                        <Icon as={PhotoIcon} w={6} h={6} color='fg.subtle' />
                        <Text fontSize='sm' color='fg.muted'>
                          Click to upload — JPG, PNG, WebP or GIF, up to 10MB
                        </Text>
                      </>
                    )}
                  </Center>
                )}
              </Box>
            </Grid>

            <Flex justify='flex-end' mt={6}>
              <Button variant='primary' onClick={() => handleSaveDetails(true)} loading={isSaving}>
                {draft.id ? 'Continue to content' : 'Create draft'}
              </Button>
            </Flex>
          </>
        )}

        {step === 'content' && (
          <>
            <Box
              bg='bg.panel'
              borderWidth='1px'
              borderColor='border'
              borderRadius='lg'
              shadow='sm'
              px={6}
              py={5}
              mb={4}
            >
              <Text
                fontFamily='heading'
                fontSize='2xs'
                fontWeight='700'
                textTransform='uppercase'
                letterSpacing='0.16em'
                color='fg.muted'
                mb={1.5}
              >
                Modules
              </Text>
              <Text fontSize='sm' color='fg.muted' maxW='2xl'>
                Name each module, tell the learner how to complete it, then pull content in from
                your libraries. Learners unlock modules in this order — drag a module to move it.
              </Text>
            </Box>

            <Stack gap={4}>
              {modules.length > 0 && (
                <SortableList
                  items={modules.map((module) => ({ id: module.id, draft: module }))}
                  onChange={handleReorderModules}
                  renderItem={({ draft: moduleDraft }) => {
                    const index = modules.findIndex((m) => m.id === moduleDraft.id);
                    return (
                      <SortableList.Item id={moduleDraft.id}>
                        <Box flex='1' minW={0}>
                          <ModuleCard
                            module={moduleDraft}
                            index={index === -1 ? 0 : index}
                            onFieldsBlur={handleModuleFieldsBlur}
                            onFieldsChange={handleModuleFieldsChange}
                            onItemsChange={handleItemsChange}
                            onRemove={setModuleToRemove}
                          />
                        </Box>
                      </SortableList.Item>
                    );
                  }}
                />
              )}

              <Button
                variant='outline'
                borderStyle='dashed'
                py={6}
                fontFamily='heading'
                textTransform='uppercase'
                letterSpacing='0.1em'
                fontSize='xs'
                onClick={handleAddModule}
              >
                <Icon as={PlusIcon} w={4} h={4} color='brand.fg' />
                Add module
              </Button>
            </Stack>

            <Flex justify='space-between' mt={6}>
              <Button variant='ghost' onClick={() => setStep('details')}>
                Back
              </Button>
              <Button variant='primary' onClick={() => setStep('access')}>
                Continue to access &amp; tracking
              </Button>
            </Flex>
          </>
        )}

        {step === 'access' && (
          <>
            <Grid
              templateColumns={{ base: '1fr', lg: 'minmax(0,1fr) 300px' }}
              gap={5}
              alignItems='start'
            >
              <Flex direction='column' gap={5}>
                <StepAccess
                  draft={draft}
                  trackingLocked={trackingLocked}
                  onPick={handleAccessPick}
                />
                {draft.id && (
                  <AssignCoursePanel
                    course={{ id: draft.id, tracking_mode: draft.tracking_mode }}
                    workspaceId={workspaceId}
                    // A draft can't be assigned yet, so selections stage and
                    // apply at publish; an already-published course assigns
                    // live, exactly like the panel on the course page.
                    staged={
                      status === 'published'
                        ? undefined
                        : { value: stagedAssignees, onChange: setStagedAssignees }
                    }
                    onAssigned={() => setTrackingLocked(true)}
                  />
                )}
                {draft.id && (
                  <RemindersCard courseId={draft.id} isOpenAccess={draft.access_mode === 'open'} />
                )}
              </Flex>

              {referenceId && (
                <Box
                  bg='bg.panel'
                  borderWidth='1px'
                  borderColor='border'
                  borderRadius='lg'
                  shadow='sm'
                  p={5}
                >
                  <Text
                    fontFamily='heading'
                    fontSize='2xs'
                    fontWeight='700'
                    textTransform='uppercase'
                    letterSpacing='0.16em'
                    color='fg.muted'
                    mb={3}
                  >
                    Shareable link
                  </Text>
                  <Box bg='bg.subtle' borderRadius='md' px={3} py={2.5} mb={3}>
                    <Text fontSize='xs' color='fg' wordBreak='break-all'>
                      {buildPublicUrl('courses', referenceId)}
                    </Text>
                  </Box>
                  <Text fontSize='xs' color='fg.muted' mb={3.5}>
                    {status !== 'published'
                      ? 'The link goes live when you publish.'
                      : draft.access_mode === 'open'
                        ? 'The link always opens the latest published version. No sign-in — completions land in the aggregate count.'
                        : 'The link always opens the latest published version. Learners sign in to their Coach Hub account first.'}
                  </Text>
                  <Button
                    variant='outline'
                    size='sm'
                    width='full'
                    onClick={async () => {
                      await navigator.clipboard.writeText(buildPublicUrl('courses', referenceId));
                      // Copying IS sharing once the course is live, so stamp
                      // shared_at the same way the share dialog does.
                      if (status === 'published' && draft.id) {
                        api.post(`courses/${draft.id}/share`).catch(() => {});
                      }
                      toaster.create({ title: 'Link copied', type: 'success' });
                    }}
                  >
                    Copy link
                  </Button>
                </Box>
              )}
            </Grid>
            <Flex justify='space-between' mt={6}>
              <Button variant='ghost' onClick={() => setStep('content')}>
                Back
              </Button>
              <Button variant='primary' onClick={() => setStep('publish')}>
                Continue to publish
              </Button>
            </Flex>
          </>
        )}

        {step === 'publish' && (
          <>
            <Grid
              templateColumns={{ base: '1fr', lg: 'minmax(0,1fr) 320px' }}
              gap={5}
              alignItems='start'
            >
              <Box
                bg='bg.panel'
                borderWidth='1px'
                borderColor='border'
                borderRadius='lg'
                shadow='sm'
                p={6}
              >
                <Heading as='h2' size='lg' mb={1}>
                  Review &amp; publish
                </Heading>
                <Text fontSize='sm' color='fg.muted' mb={5}>
                  Check everything reads the way a learner will see it.
                </Text>
                <Stack gap={0}>
                  {[
                    { label: 'Title', value: draft.name || '—', step: 'details' as StepKey },
                    {
                      label: 'Description',
                      value: draft.description || '—',
                      step: 'details' as StepKey,
                    },
                    {
                      label: 'Audience',
                      value: draft.audience_type || 'Everyone in the workspace',
                      step: 'details' as StepKey,
                    },
                    {
                      label: 'Content',
                      value: `${modules.length} ${modules.length === 1 ? 'module' : 'modules'} · ${modules.reduce((sum, m) => sum + m.items.length, 0)} items${modules.some((m) => m.assignment_text) ? ' · assignments' : ''}`,
                      step: 'content' as StepKey,
                    },
                    {
                      label: 'Access',
                      value:
                        draft.access_mode === 'open' ? 'Open link — no sign-in' : 'Log-in required',
                      step: 'access' as StepKey,
                    },
                    {
                      label: 'Tracking',
                      value:
                        draft.tracking_mode === 'individual'
                          ? 'Individually tracked'
                          : 'Completed count only',
                      step: 'access' as StepKey,
                    },
                    ...(stagedAssignees.users.length + stagedAssignees.groups.length > 0
                      ? [
                          {
                            label: 'Assigned to',
                            value: `${[
                              ...stagedAssignees.groups.map((group) => group.name),
                              ...stagedAssignees.users.map(
                                (user) =>
                                  `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim() ||
                                  user.email,
                              ),
                            ].join(', ')} — sent on publish`,
                            step: 'access' as StepKey,
                          },
                        ]
                      : []),
                  ].map((row) => (
                    <Grid
                      key={row.label}
                      templateColumns='140px minmax(0,1fr) auto'
                      gap={4}
                      py={3.5}
                      borderTopWidth='1px'
                      borderColor='border'
                      alignItems='start'
                    >
                      <Text
                        fontFamily='heading'
                        fontSize='2xs'
                        fontWeight='700'
                        textTransform='uppercase'
                        letterSpacing='0.1em'
                        color='fg.muted'
                        pt={0.5}
                      >
                        {row.label}
                      </Text>
                      <Text fontSize='sm' color='fg'>
                        {row.value}
                      </Text>
                      <Button variant='ghost' size='xs' onClick={() => setStep(row.step)}>
                        Edit
                      </Button>
                    </Grid>
                  ))}
                </Stack>
              </Box>

              <Box
                bg='bg.panel'
                borderWidth='1px'
                borderColor='border'
                borderRadius='lg'
                shadow='sm'
                p={5}
              >
                <Text
                  fontFamily='heading'
                  fontSize='2xs'
                  fontWeight='700'
                  textTransform='uppercase'
                  letterSpacing='0.16em'
                  color='fg.muted'
                  mb={2.5}
                >
                  Before you publish
                </Text>
                {status === 'published' ? (
                  <Text fontSize='sm' fontWeight='600' color='success' mb={2.5}>
                    This course is live on version {version}.
                  </Text>
                ) : null}
                <Text fontSize='sm' color='fg.muted' mb={2.5}>
                  {status === 'published'
                    ? `Content edits already apply to everyone. Publishing again records version ${
                        version + 1
                      } in the history so the tracking roster can tell who finished on which version.`
                    : `Publishing creates version ${version + 1}${
                        version === 0 ? ' — the course goes live.' : ' in the version history.'
                      } Nobody is restarted: everyone's progress carries straight over.`}
                </Text>
                <Text fontSize='sm' color='fg.muted' mb={4}>
                  Access and tracking lock at first share. You can keep editing content after
                  publishing — changes apply to everyone, including learners partway through.
                </Text>
                <Field
                  label='Version note'
                  helperText='Shown in the version history. Optional.'
                  mb={4}
                >
                  <Textarea
                    rows={2}
                    maxLength={500}
                    value={publishNote}
                    placeholder={
                      version === 0
                        ? 'Ready for the season.'
                        : 'What changed? e.g. Swapped the intro video.'
                    }
                    onChange={(e) => setPublishNote(e.target.value)}
                  />
                </Field>
                <Stack gap={2.5}>
                  <Button variant='primary' width='full' onClick={handlePublish} loading={isSaving}>
                    {status === 'published' ? `Publish version ${version + 1}` : 'Publish course'}
                  </Button>
                  {status === 'published' && (
                    <Button
                      variant='outline'
                      size='sm'
                      width='full'
                      disabled={isSaving}
                      onClick={async () => {
                        if (!draft.id) return;
                        await setCourseStatus(draft.id, 'draft');
                        setStatus('draft');
                        toaster.create({ title: 'Course moved back to draft', type: 'success' });
                      }}
                    >
                      Unpublish
                    </Button>
                  )}
                </Stack>
              </Box>
            </Grid>

            <Flex justify='flex-start' mt={6}>
              <Button variant='ghost' onClick={() => setStep('access')}>
                Back
              </Button>
            </Flex>
          </>
        )}
      </Container>

      <DestructiveModal
        isOpen={moduleToRemove !== null}
        onCancel={() => setModuleToRemove(null)}
        onConfirm={handleConfirmRemoveModule}
        title='Remove module'
        confirmText='Remove'
      >
        <Text fontSize='sm' color='fg.muted'>
          Remove <b>{moduleToRemove?.name || 'this module'}</b>? Its items and completion records go
          with it.
        </Text>
      </DestructiveModal>
    </Box>
  );
}
